import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { LinkItemWrapper, LinkText } from "./LinkItemWrapper";
import { NavigationUtils, TrackingUtils } from "../../../../utils";
import { AccountActionCreators } from "../../../../redux/action-creators";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "styled-components";
import Arrow from "../../../common/Arrow/Arrow";
import SVGIconV3 from "../../../common/SVGIcon/SVGIconV3";
import { AccountSelectors } from "../../../../redux/selectors";

const LinkItem = props => {
  const {
    iconName,
    linkText,
    handleClick,
    isExpandable,
    expanded,
    dataTestId,
    href,
    trackingInfo,
    children,
    showArrow,
    iconColor = null,
    isArrowStatic = null
  } = props;

  const drawerActive = useSelector(AccountSelectors.drawerActive);

  const dispatch = useDispatch();
  const theme = useTheme();

  const [arrowDirection, setArrowDirection] = useState("right");
  const [color, setColor] = useState(theme.colors.textColor);
  const [isHover, setIsHover] = useState(false);

  const isArrowShouldChangeDirection = useCallback(() => {
    return !isArrowStatic;
  }, [isArrowStatic]);

  const onClickHandler = evt => {
    if (!isExpandable && drawerActive) {
      dispatch(AccountActionCreators.toggleAccountDrawer());
    } else {
      setColor(theme.colors.brandColor);
      if (isArrowShouldChangeDirection()) {
        setArrowDirection(expanded ? "down" : "up");
      }
    }

    TrackingUtils.sendAnalytics({
      dataCategory: trackingInfo.dataCategory,
      dataAction: "click",
      dataLabel: trackingInfo.dataLabel,
      dataValue: ""
    });

    if (typeof handleClick === "function") handleClick(evt);
    else if (href) {
      const RgExp = new RegExp("^(?:[a-z]+:)?//", "i");
      if (RgExp.test(href)) {
        NavigationUtils.openExternalUrl(href);
      } else {
        NavigationUtils.navigateTo(href);
      }
    }
  };

  useEffect(() => {
    if (isExpandable && expanded) {
      setColor(theme.colors.brandColor);
      setArrowDirection("up");
    } else if (isHover) setColor(theme.colors.hooverLinkColors);
    else if (isExpandable && !expanded) {
      setColor(theme.colors.textColor);
      setArrowDirection("down");
    } else setColor(theme.colors.textColor);
  }, [
    isHover,
    isExpandable,
    expanded,
    theme.colors.hooverLinkColors,
    theme.colors.textColor,
    theme.colors.brandColor
  ]);

  const getColor = useCallback(() => {
    if (iconColor) {
      return iconColor;
    } else if (theme.linkItem.icon.defaultColor) {
      return theme.linkItem.icon.defaultColor;
    } else {
      return color;
    }
  }, [iconColor, color, theme.linkItem.icon.defaultColor]);

  return (
    <LinkItemWrapper
      onClick={onClickHandler}
      isExpandable={isExpandable}
      expanded={expanded}
      data-testid={dataTestId}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      {iconName && (
        <SVGIconV3
          iconName={iconName}
          width="1.7rem"
          height="1.7rem"
          color={getColor()}
        ></SVGIconV3>
      )}
      {linkText && (
        <LinkText isExpandable={isExpandable} expanded={expanded} color={color}>
          {linkText}
        </LinkText>
      )}
      {children}
      {showArrow !== false && (
        <Arrow color={color} direction={arrowDirection}></Arrow>
      )}
    </LinkItemWrapper>
  );
};

LinkItem.propTypes = {
  iconName: PropTypes.string,
  linkText: PropTypes.string,
  handleClick: PropTypes.func,
  isExpandable: PropTypes.bool,
  expanded: PropTypes.bool,
  dataTestId: PropTypes.string,
  href: PropTypes.string,
  trackingInfo: PropTypes.object,
  children: PropTypes.node,
  showArrow: PropTypes.bool,
  iconColor: PropTypes,
  isArrowStatic: PropTypes.bool
};

export default React.memo(LinkItem);
